/**
 * @fileoverview Certifications component for managing company certifications and capabilities.
 * This component handles various business certifications, industry sectors, experience levels,
 * delivery times, sales volumes, payment methods, insurance, permits, and implemented practices.
 */

import styles from "./Certifications.module.css";
import useCertificationsSection from "./useCertificationsSection";
import CertificationsCheckboxes from "./CertificationsCheckboxes";
import DeliveryAndSalesFields from "./DeliveryAndSalesFields";
import InsuranceAndPracticesFields from "./InsuranceAndPracticesFields";
import BusinessAssociationField from "./BusinessAssociationField";
import SectorExperienceSection from "./SectorExperienceSection";
import MainCustomersField from "./MainCustomersField";
import DocumentedSystemField from "./DocumentedSystemField";

/**
 * Certifications component for displaying and editing company certifications and capabilities.
 * Manages business certifications, industry sectors, years of experience, delivery times,
 * sales volumes, payment methods, insurance coverage, permits, and business practices.
 *
 * @param {Object} props - Component props
 * @param {Object} props.company - Company data object containing certification fields
 * @param {Function} props.onFieldChange - Callback function triggered when a field value changes
 * @param {Function} props.onValidationChange - Callback function triggered when validation state changes
 * @param {number} [props.selectedProfile=0] - Currently selected company profile index for multi-profile support
 * @returns {JSX.Element} The rendered Certifications form section
 */
const Certifications = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const section = useCertificationsSection({
    company,
    onFieldChange,
    onValidationChange,
    selectedProfile,
  });
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth >= 481 && window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;
  const isMobile = isMediumMobile || isSmallMobile;

  return (
    <div className={styles.sectionContent}>
      <CertificationsCheckboxes
        section={section}
        company={company}
        isTablet={isTablet}
      />
      <DeliveryAndSalesFields
        section={section}
        selectedProfile={selectedProfile}
      />
      <InsuranceAndPracticesFields section={section} isTablet={isTablet} />
      <BusinessAssociationField
        section={section}
        company={company}
        selectedProfile={selectedProfile}
        isTablet={isTablet}
        isMobile={isMobile}
      />
      <SectorExperienceSection section={section} isTablet={isTablet} />
      <div
        className={`${styles.inputRow} ${
          isTablet ? styles.tabletInputRow : ""
        }`}
      >
        <MainCustomersField section={section} company={company} />
        <DocumentedSystemField section={section} />
      </div>
    </div>
  );
};

export default Certifications;
